import { Link } from 'react-router-dom';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Twitter, 
  Linkedin, 
  Instagram, 
  Award, 
  Shield, 
  Leaf, 
  Clock, 
} from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Sustainability', path: '/sustainability' },
    { name: 'Clientele', path: '/clientele' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Contact', path: '/contact' }
  ];

  const services = [
    'Domestic Waste Handling',
    'Medical Waste Disposal',
    'Transportation & Logistics',
    'Incineration Services',
    'Plastic Shredding',
    'Training & Consultancy',
    'e-Waste Management',
    'Supply of Bin Liners'
  ];

  const certifications = [
    'PPDA Certified',
    'NEMA Approved',
    'KCCA Licensed',
    'NDA Compliant'
  ];

  return (
    <footer className="bg-gray-900 text-white w-full overflow-x-hidden">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 md:px-8 xl:px-12 py-10 sm:py-14 md:py-16 xl:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8 md:gap-10">
          {/* Company Info */}
          <div className="lg:col-span-1 sm:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center space-x-2 mb-4 sm:mb-6">
              <img src="./logo.png" alt="BioHaz Waste Solutions Logo" className="h-12 w-auto" />
            </Link>
            <p className="text-gray-300 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
              Uganda's leading biohazard and hazardous waste management company, 
              committed to transforming waste into valuable resources while protecting 
              our environment and communities.
            </p>
            <div className="flex space-x-4">
              <a href="https://x.com/biohazwaste" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-400 transition-colors">
                <Twitter className="w-4 h-4 sm:w-5 sm:h-5" />
              </a>
              <a href="https://www.instagram.com/biohazwaste/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-pink-500 transition-colors">
                <Instagram className="w-4 h-4 sm:w-5 sm:h-5" />
              </a>
              <a href="https://www.linkedin.com/company/biohazwaste" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-500 transition-colors">
                <Linkedin className="w-4 h-4 sm:w-5 sm:h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-base sm:text-lg mb-4 sm:mb-6">Quick Links</h3>
            <ul className="space-y-2 sm:space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.path} 
                    className="text-gray-300 hover:text-blue-400 transition-colors text-sm sm:text-base"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-semibold text-base sm:text-lg mb-4 sm:mb-6">Our Services</h3>
            <ul className="space-y-2 sm:space-y-3">
              {services.map((service, index) => (
                <li key={index} className="text-gray-300 text-xs sm:text-sm">
                  {service}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-semibold text-base sm:text-lg mb-4 sm:mb-6">Contact Info</h3>
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 mt-1 flex-shrink-0" />
                <div className="text-gray-300 text-xs sm:text-sm">
                  <p>Plot 27, Valley Drive</p>
                  <p>Minister's Village, Ntinda</p>
                  <p>Kampala, Uganda</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                <div className="text-gray-300 text-xs sm:text-sm">
                  <a href="tel:+256393254458" className="hover:text-blue-400 transition-colors block">
                    +256 393 254458
                  </a>
                  <a href="tel:+256740825445" className="hover:text-blue-400 transition-colors block">
                    +256 740 825445
                  </a>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                <Link 
                  to="/" 
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                >
                  info@biohazwaste.com
                </Link>
              </div>
              <div className="flex items-center space-x-3 group">
                <div className="w-12 h-12 rounded-full bg-[#295cab] flex items-center justify-center group-hover:bg-[#1e4a8a] transition-colors duration-300">
                  <Clock className="w-6 h-6" />
                </div>
                <div className="text-gray-300 text-xs sm:text-sm">
                  <p>24/7 Emergency Response</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Certifications & Values */}
        <div className="border-t border-gray-800 mt-8 sm:mt-12 pt-8 sm:pt-12 w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {/* Certifications */}
            <div>
              <h4 className="font-semibold text-blue-400 mb-3 sm:mb-4 flex items-center space-x-2">
                <Award className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base">Certifications</span>
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {certifications.map((cert, index) => (
                  <span key={index} className="text-gray-300 text-xs sm:text-sm">
                    {cert}
                  </span>
                ))}
              </div>
            </div>

            {/* Values */}
            <div>
              <h4 className="font-semibold text-blue-400 mb-3 sm:mb-4 flex items-center space-x-2">
                <Shield className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base">Our Values</span>
              </h4>
              <div className="text-gray-300 text-xs sm:text-sm space-y-1">
                <p>Safety • Integrity • Innovation</p>
                <p>Sustainability • Customer Focus</p>
              </div>
            </div>

            {/* Environmental Commitment */}
            <div className="sm:col-span-2 lg:col-span-1">
              <h4 className="font-semibold text-blue-400 mb-3 sm:mb-4 flex items-center space-x-2">
                <Leaf className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base">Environmental Impact</span>
              </h4>
              <div className="text-gray-300 text-xs sm:text-sm space-y-1">
                <p>85% Waste Diverted from Landfills</p>
                <p>60% Emission Reduction</p>
                <p>Carbon Neutral Operations by 2024</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 md:px-8 xl:px-12 py-3 sm:py-5 md:py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-2 md:gap-0">
            <p className="text-gray-400 text-xs sm:text-sm text-center sm:text-left">
              {currentYear} BioHaz Waste Solutions. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center sm:justify-end space-x-4 sm:space-x-6 text-xs sm:text-sm">
              <Link 
                to="/privacy-policy" 
                className="text-gray-400 hover:text-[#295cab] transition-colors"
                onClick={() => window.scrollTo(0, 0)}
              >
                Privacy Policy
              </Link>
              <Link 
                to="/terms-of-service" 
                className="text-gray-400 hover:text-[#295cab] transition-colors"
                onClick={() => window.scrollTo(0, 0)}
              >
                Terms of Service
              </Link>
              <Link 
                to="/cookie-policy" 
                className="text-gray-400 hover:text-[#295cab] transition-colors"
                onClick={() => window.scrollTo(0, 0)}
              >
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;